// Bounded backstop for navigation that lands after the synchronous span below
// (a confirmed action whose route change happens in a later effect/tick).
const SUPPRESSION_TAIL_MS = 100;

// Module-level, so it covers every mounted navigation blocker at once: nested
// trackers (a dirty Modal inside a dirty Stepper) each patch history
// independently, and a navigation the app itself just performed must not be
// intercepted by any of them.
//
// Depth, not a bare deadline: nested calls each hold their own claim, and the
// hold cannot expire early because the main thread stalled mid-navigation.
let suppressionDepth = 0;
let suppressedUntil = 0;

/**
 * Runs `fn` with unsaved-changes navigation interception disabled, holding the
 * suppression for the whole synchronous span (and, if `fn` returns a promise,
 * until it settles) so the resulting history change — and the popstate React
 * Router reacts to — also lands unguarded.
 *
 * Use this for navigation the app performs on the user's behalf *after* the
 * guard has already had its say — a confirmed discard, or the Stepper mirroring
 * its own state into the URL. It is not a way to skip the guard for navigation
 * the user initiated.
 *
 * @param {() => any} fn
 */
export const suppressNavigationGuard = (fn) => {
  suppressionDepth += 1;
  let released = false;

  // Idempotent, and stamps the tail on the way out so a navigation deferred past
  // this call still lands unguarded.
  const release = () => {
    if (released) return;
    released = true;
    suppressionDepth = Math.max(0, suppressionDepth - 1);
    suppressedUntil = Date.now() + SUPPRESSION_TAIL_MS;
  };

  let result;
  try {
    result = fn?.();
  } catch (error) {
    release();
    throw error;
  }

  if (result && typeof result.then === 'function') {
    return result.then(
      (value) => {
        release();
        return value;
      },
      (error) => {
        release();
        throw error;
      }
    );
  }

  release();
  return result;
};

export const isNavigationGuardSuppressed = () =>
  suppressionDepth > 0 || Date.now() < suppressedUntil;

// Long enough to cover a browser that dispatches beforeunload a little after the navigation call,
// short enough that a navigation another guard cancels does not leave the page unguarded.
const UNLOAD_ALLOWANCE_MS = 3000;

let unloadAllowedUntil = 0;

/**
 * Lets the page unload once without the unsaved-changes prompt, for a reload or redirect the user
 * has just confirmed in the app's own dialog. Time-bounded rather than consumed, so every
 * beforeunload listener firing for that one unload sees it.
 */
export const allowNextUnload = () => {
  unloadAllowedUntil = Date.now() + UNLOAD_ALLOWANCE_MS;
};

export const isUnloadAllowed = () => Date.now() < unloadAllowedUntil;
